import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLogoSettings } from '@/hooks/useLogoSettings';

interface UnifiedLogoProps {
  /** نوع الشعار - فاتح أم داكن أم تلقائي */
  variant?: 'light' | 'dark' | 'auto';
  
  /** موضع الشعار - يؤثر على الحجم */
  position?: 'header' | 'footer' | 'custom';
  
  /** حجم مخصص للشعار */
  customSize?: string;
  
  /** حجم مسبق التعريف */
  size?: 'small' | 'medium' | 'large';
  
  /** classes CSS إضافية */
  className?: string;
  
  /** تعطيل الرابط إلى الصفحة الرئيسية */
  disableLink?: boolean;
  
  /** تعطيل التأثيرات المتحركة */
  disableAnimations?: boolean;
  
  /** رابط مخصص بدلاً من الصفحة الرئيسية */
  href?: string;
  
  /** نص بديل مخصص */
  altText?: string;
}

const UnifiedLogo: React.FC<UnifiedLogoProps> = ({
  variant = 'auto',
  position = 'header', 
  customSize,
  size,
  className = '',
  disableLink = false,
  disableAnimations = false,
  href = '/',
  altText
}) => {
  const {
    logoUrl,
    siteName,
    animationEnabled,
    hoverEffects,
    transitionDuration,
    getSizeClasses,
    getEffectClasses,
    hasLogo,
    currentVariant,
    sizes,
    clampLogoSize
  } = useLogoSettings({ variant, position });

  // تحديث عند تغيير حجم النافذة فقط (بدون تحديث دوري)
  const [viewportWidth, setViewportWidth] = React.useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 0
  );
  React.useEffect(() => {
    let timeoutId: number | undefined;
    const handleResize = () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        setViewportWidth(window.innerWidth);
      }, 120);
    };
    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // تحديد الحجم النهائي
  const getFinalSize = (): string => {
    if (customSize) return customSize;
    
    // إذا كان في الهيدر الداخلي، استخدم نفس أحجام الهيدر الرئيسي
    if (position === 'header') {
      let finalSize: string;
      if (viewportWidth >= 1024) {
        finalSize = sizes.desktop.height;
      } else if (viewportWidth >= 768) {
        finalSize = sizes.tablet.height;
      } else {
        finalSize = sizes.mobile.height;
      }
      
      return clampLogoSize(finalSize);
    }
    
    if (size) {
      const sizeMap = {
        small: position === 'footer' ? '14px' : sizes.mobile.height,
        medium: position === 'footer' ? '18px' : sizes.tablet.height, 
        large: position === 'footer' ? '20px' : sizes.desktop.height
      };
      const selectedSize = sizeMap[size];
      return clampLogoSize(selectedSize);
    }
    
    // استخدام الأحجام من قاعدة البيانات مع تطبيق الحد الأقصى
    let finalSize: string;
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 1024) {
        finalSize = sizes.desktop.height;
      } else if (window.innerWidth >= 768) {
        finalSize = sizes.tablet.height;
      } else {
        finalSize = sizes.mobile.height;
      }
    } else {
      finalSize = sizes.mobile.height; // افتراضي للخادم
    }
    
    return clampLogoSize(finalSize);
  };

  // الحصول على classes CSS
  const sizeClasses = getSizeClasses(getFinalSize());
  const effectClasses = getEffectClasses();
  const shouldAnimate = animationEnabled && !disableAnimations;

  // تحديد النص البديل
  const finalAltText = altText || `${siteName} Logo` || 'Website Logo';

  // إعدادات الحركة المستقرة
  const motionProps = shouldAnimate ? {
    whileHover: hoverEffects ? { 
      scale: 1.02
    } : undefined,
    whileTap: { scale: 0.98 },
    transition: { 
      duration: 0.2
    }
  } : {};

  // تطبيق فلتر الألوان للشعار الأبيض
  const imageFilter = currentVariant === 'light' 
    ? 'brightness(0) invert(1) drop-shadow-sm' 
    : '';

  // عنصر الشعار
  const LogoElement = () => (
    <motion.div 
      className={`relative flex items-center ${className}`}
      {...motionProps}
    >
      {hasLogo ? (
        shouldAnimate ? (
          <motion.img
            src={logoUrl}
            alt={finalAltText}
            className={`${sizeClasses} ${effectClasses} ${imageFilter}`.trim()}
            style={{ height: getFinalSize() }}
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
        ) : (
          <img
            src={logoUrl}
            alt={finalAltText}
            className={`${sizeClasses} ${effectClasses} ${imageFilter}`.trim()}
            style={{ height: getFinalSize() }}
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
        )
      ) : (
        <motion.div
          className={`
            ${effectClasses}
            text-2xl font-bold
            ${currentVariant === 'light' 
              ? 'text-white drop-shadow-md' 
              : 'text-foreground drop-shadow-sm'
            }
          `.replace(/\s+/g, ' ').trim()}
        >
          {siteName}
        </motion.div>
      )}
    </motion.div>
  );

  // إرجاع الشعار مع أو بدون رابط
  if (disableLink) {
    return <LogoElement />;
  }

  return (
    <Link to={href} className="flex items-center">
      <LogoElement />
    </Link>
  );
};

export default UnifiedLogo;

/**
 * شعار مخصص للهيدر
 */
export const HeaderLogo: React.FC<Omit<UnifiedLogoProps, 'position'>> = (props) => (
  <UnifiedLogo {...props} position="header" />
);

/**
 * شعار مخصص للفوتر
 */
export const FooterLogo: React.FC<Omit<UnifiedLogoProps, 'position'>> = (props) => (
  <UnifiedLogo {...props} position="footer" />
);

/**
 * شعار بسيط بدون رابط
 */
export const SimpleLogo: React.FC<Omit<UnifiedLogoProps, 'disableLink'>> = (props) => (
  <UnifiedLogo {...props} disableLink />
);